import { Purchases, LOG_LEVEL, INTRO_ELIGIBILITY_STATUS } from "@revenuecat/purchases-capacitor";
import { isNativeiOS } from "../utils/platform";

const ENTITLEMENT_ID = "premium";

class RevenueCatService {
  static instance = null;

  static getInstance() {
    if (!RevenueCatService.instance) {
      RevenueCatService.instance = new RevenueCatService();
    }
    return RevenueCatService.instance;
  }

  constructor() {
    this.isInitialized = false;
    this.currentUserId = null;
    this.customerInfo = null;
    this.offerings = null;
    this.apiKey = process.env.REACT_APP_REVENUECAT_IOS_API_KEY;
  }

  async initialize(userId) {
    if (!isNativeiOS()) {
      console.log("[RC] Skipping RevenueCat init — not on iOS");
      return;
    }

    if (!userId || typeof userId !== "string" || userId.trim() === "") {
      throw new Error("RevenueCat requires an authenticated user ID");
    }

    if (!this.apiKey) {
      console.error("[RC] REACT_APP_REVENUECAT_IOS_API_KEY is not set");
      throw new Error("RevenueCat API key not configured");
    }

    if (this.isInitialized && this.currentUserId === userId) {
      console.log("[RC] Already initialized for this user");
      return;
    }

    if (this.isInitialized && this.currentUserId !== userId) {
      console.log("[RC] Switching users — logging out previous user");
      await this.logout();
    }

    this.currentUserId = userId;

    try {
      await Purchases.setLogLevel({ level: LOG_LEVEL.DEBUG });

      console.log("[RC] Configuring with key:", this.apiKey?.substring(0, 10) + "...", "user:", userId);
      await Purchases.configure({
        apiKey: this.apiKey,
        appUserID: userId,
        observerMode: false,
        usesStoreKit2IfAvailable: true,
      });
      console.log("[RC] Configure complete");

      await Purchases.logIn({ appUserID: userId });
      console.log("[RC] LogIn complete");

      await this.refreshOfferings();
      await this.refreshCustomerInfo();

      this.isInitialized = true;
      console.log("[RC] Initialized for user:", userId);
    } catch (error) {
      console.error("[RC] Initialization failed:", error);
      console.error("[RC] Error details:", JSON.stringify(error, Object.getOwnPropertyNames(error)));
      this.currentUserId = null;
      this.customerInfo = null;
      throw error;
    }
  }

  async refreshOfferings() {
    try {
      const result = await Purchases.getOfferings();
      console.log("[RC] Raw offerings result:", JSON.stringify(result, null, 2)?.substring(0, 500));
      this.offerings = result.current;

      if (this.offerings) {
        console.log(
          "[RC] Loaded offerings:",
          this.offerings.availablePackages?.length,
          "packages"
        );
        if (this.offerings.availablePackages?.length) {
          this.offerings.availablePackages.forEach((pkg) => {
            const p = pkg.product;
            console.log("[RC] Package:", pkg.identifier, "product:", p?.identifier, "price:", p?.priceString);
            console.log("[RC]   introPrice:", JSON.stringify(p?.introPrice || p?.introductoryPrice || "none"));
            console.log("[RC]   discounts:", JSON.stringify(p?.discounts || "none"));
            console.log("[RC]   product keys:", p ? Object.keys(p) : "N/A");
          });
        }
      } else {
        console.warn("[RC] No current offering found — check RevenueCat dashboard: ensure an offering is set as 'Current'");
        console.warn("[RC] All offerings keys:", result.all ? Object.keys(result.all) : "none");
      }

      return this.offerings;
    } catch (error) {
      console.error("[RC] Failed to load offerings:", error);
      console.error("[RC] Offerings error details:", JSON.stringify(error, Object.getOwnPropertyNames(error)));
      return null;
    }
  }

  async refreshCustomerInfo() {
    try {
      const result = await Purchases.getCustomerInfo();
      this.customerInfo = result.customerInfo || result;
      return this.customerInfo;
    } catch (error) {
      console.error("[RC] Failed to refresh customer info:", error);
      return null;
    }
  }

  getPackages() {
    if (!this.offerings) return [];
    return this.offerings.availablePackages || [];
  }

  isSubscribed() {
    if (!this.customerInfo) return false;
    const active = this.customerInfo.entitlements?.active;
    return !!(active && active[ENTITLEMENT_ID]);
  }

  async purchasePackage(pkg) {
    if (!this.isInitialized) {
      throw new Error("RevenueCat not initialized");
    }

    try {
      console.log("[RC] Purchasing package:", pkg.identifier || pkg.product?.identifier);

      const result = await Purchases.purchasePackage({ aPackage: pkg });

      this.customerInfo = result.customerInfo || result;

      const isPremium = this.isSubscribed();
      console.log("[RC] Purchase complete, premium:", isPremium);

      return { success: true, customerInfo: this.customerInfo };
    } catch (error) {
      if (
        error.code === "USER_CANCELLED" ||
        error.message?.includes("cancelled") ||
        error.message?.includes("canceled")
      ) {
        console.log("[RC] Purchase cancelled by user");
        return { success: false, cancelled: true };
      }

      console.error("[RC] Purchase failed:", error);
      return {
        success: false,
        cancelled: false,
        error: error.message || "Purchase failed",
      };
    }
  }

  async checkIntroEligibility(productIdentifiers) {
    if (!this.isInitialized) return {};
    try {
      const result = await Purchases.checkTrialOrIntroductoryPriceEligibility({
        productIdentifiers,
      });
      console.log("[RC] Intro eligibility result:", JSON.stringify(result));
      return result;
    } catch (error) {
      console.error("[RC] Intro eligibility check failed:", error);
      return {};
    }
  }

  async restorePurchases() {
    if (!this.isInitialized) {
      throw new Error("RevenueCat not initialized");
    }

    try {
      console.log("[RC] Restoring purchases...");
      const result = await Purchases.restorePurchases();
      this.customerInfo = result.customerInfo || result;

      const isPremium = this.isSubscribed();
      console.log("[RC] Restore complete, premium:", isPremium);

      return { success: true, isPremium };
    } catch (error) {
      console.error("[RC] Restore failed:", error);
      return { success: false, error: error.message || "Restore failed" };
    }
  }

  async logout() {
    try {
      if (this.isInitialized) {
        await Purchases.logOut();
      }
    } catch (error) {
      console.error("[RC] Logout error:", error);
    } finally {
      this.currentUserId = null;
      this.customerInfo = null;
      this.offerings = null;
      this.isInitialized = false;
    }
  }
}

export default RevenueCatService;
